import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../../utils';
import { BackendEmail, BackendEmailFilters } from '../../../../../../../models/backend-email';
import { ListResponse } from '../../../../../../../models/list-response';
import {
  BackendEmailDetailModalComponent
} from './modals/backend-email-detail-modal/backend-email-detail-modal.component';

@Component({
  selector: 'app-customer-emails-sub-list',
  templateUrl: './customer-emails-sub-list.component.html',
  styleUrls: ['./customer-emails-sub-list.component.css']
})
export class CustomerEmailsSubListComponent implements OnInit {

  public pageNumber = 1;
  @LocalStorage() public pageSize;

  public filtersChangedSubject = new Subject<null>();

  public filters: BackendEmailFilters = {};

  public customerId: number;

  public emailsList: ListResponse<BackendEmail>;
  public loadingEmails = false;

  public customerNotFound = false;
  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  constructor(
    public accountService: AccountService,
    private route: ActivatedRoute,
    private customerService: CustomerService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }
    const routeParams = this.route.parent.snapshot.params;
    this.customerId = parseInt(routeParams.id, 10);
    if (isNaN(this.customerId)) {
      this.customerNotFound = true;
      return;
    }

    if (!this.pageSize) {
      this.pageSize = 10;
    }
    // noinspection SillyAssignmentJS
    this.pageSize = this.pageSize;

    this.filtersChangedSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.loadEmails();
    });

    this.loadEmails();
  }

  public openEmail(email: BackendEmail) {
    const modalRef = this.modalService.open(
      BackendEmailDetailModalComponent,
      { size: 'lg' }
    );
    modalRef.componentInstance.emailId = email.id;
  }

  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  public handleFilterChange() {
    this.pageNumber = 1;
    this.filtersChangedSubject.next();
  }

  public lastPageNumber() {
    if (!this.emailsList) { return 1; }
    return Math.ceil(this.emailsList.count / this.pageSize);
  }

  private loadEmails() {
    this.loadingEmails = true;

    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.customerService
      .getCustomerBackendEmails(this.customerId, this.pageNumber, this.pageSize, this.filters)
      .then(response => {
        if (thisRequestCount === this.listRequestCount) {
          this.emailsList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingEmails = false;
        }
      });
  }
}
